// Fleet's agents and models as one page: the range's totals, its sessions
// split by kind, and one register of agents or models whose bars split into
// what each row used and unfold into it. It reads the app's range, as the
// Overview does (lib/fleetConsoleStore.js fleetWindows), and counts it as the
// Overview does, over the breakdown's split (lib/fleetBreakdown.js). Local inference (FleetLocal) and Endpoints
// (FleetEndpoints) are built of the same parts, exported here.
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DateRangePicker from "./DateRangePicker.jsx";
import { AgentIcon } from "./Dimension.jsx";
import EmptyState from "./EmptyState.jsx";
import HoverTooltip from "./HoverTooltip.jsx";
import { useElementSize } from "./kernelPrimitives/shared.jsx";
import { buttonClass } from "./ui/Button.jsx";
import SearchInput from "./ui/SearchInput.jsx";
import { SkeletonRows } from "./ui/Skeleton.jsx";
import Tabs from "./ui/Tabs.jsx";
import { useQuery } from "../lib/dataClient.js";
import { DEFAULT_RANGE } from "../lib/dateRangePresets.js";
import { FLEET_STALE_MS, VIEW_LABEL } from "../lib/fleetConsole.js";
import { fleetWindows, setFleet } from "../lib/fleetConsoleStore.js";
import { SEGMENTS as PARTS, STEP, bandOf, beforeOf, bigChange, exactGroups, paged, pagesTo, rowsOf, segmentsOf, shareText, sortRows, totalsOf, unusedRows } from "../lib/fleetList.js";
import { KINDS, exploreFor, placeLabels, reachedAs, split } from "../lib/fleetBreakdown.js";
import { useFleetPivots } from "../lib/fleetPivots.js";
import { agentsOf, tracesIn } from "../lib/overview.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";

export const NUM = new Intl.NumberFormat("en-US");
export const MUTED = "text-[color:var(--muted)]";
export const INK = "text-[color:var(--ink)]";
export const BUTTON = `${buttonClass("secondary", "sm")} pointer-coarse:h-[44px]`;
export const ROW = "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[color:var(--green)]";
// The register: name, sessions, change, the bar.
export const LINE = "grid w-full grid-cols-[minmax(0,208px)_64px_84px_minmax(0,1fr)] items-center gap-x-[16px] px-[8px] text-left max-[640px]:grid-cols-[minmax(0,1fr)_56px_minmax(0,1fr)]";
export const SORTS = [["most", "Most traces"], ["newest", "Newest"], ["fewest", "Fewest traces"]];
// What a row's bar is split into.
const USED = { agents: "models", models: "agents" };

export const change = (now, then) => (then == null || now === then ? "" : `${now > then ? "↑" : "↓"} ${NUM.format(Math.abs(now - then))}`);
const pct = (part, whole) => `${whole ? Math.min(100, (part / whole) * 100) : 0}%`;
const endpointsOf = (n) => `${NUM.format(n)} ${n === 1 ? "endpoint" : "endpoints"}`;
// The kinds a row ran in, the most sessions first, as one phrase.
function phrase(ids) {
  const words = ids.map((id, i) => (i ? KINDS[id].toLowerCase() : KINDS[id]));
  return words.length < 2 ? words[0] || "" : `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`;
}

let pen;
// A label's width in the page's type, to place it in its part or beside it.
function textWidth(text, size, weight = 400) {
  if (pen === undefined) pen = globalThis.document?.createElement("canvas").getContext?.("2d") || null;
  if (!pen) return String(text).length * size * 0.58;
  pen.font = `${weight} ${size}px Inter, system-ui, sans-serif`;
  return pen.measureText(String(text)).width;
}

// The range's groups and the span before's, counted as the Overview counts
// them (lib/fleetList.js exactGroups): the traces are the Overview's and
// Findings' own read by hour, and each agent's the Overview's, so a click from
// one page to the other shows the same numbers. Read with Fleet's staleness,
// what the Overview has just read is what Fleet shows.
export function useBlock(range) {
  const win = fleetWindows(range);
  const caps = useMyPermissions();
  const access = { inventory: hasPermission(caps, PERMISSIONS.ENDPOINT_METADATA_READ), events: hasPermission(caps, PERMISSIONS.EVENTS_READ) };
  const traces = useQuery("getOverviewTraces", { days: win.w.days }, { staleMs: FLEET_STALE_MS });
  const agents = useQuery("getOverviewAgents", { now: win.w.now, before: win.w.before }, { staleMs: FLEET_STALE_MS });
  const groups = useQuery("getFleetBreakdown", { range: win.now, access }, { staleMs: FLEET_STALE_MS });
  // No span before where the range and it pass the widest window read (the Overview's rule).
  const prior = useQuery("getFleetBreakdown", { range: win.then, access, before: true }, { enabled: !!win.then, staleMs: FLEET_STALE_MS });
  const then = win.then && !prior.previousParams ? prior.data?.then ?? null : null;
  // While a new range reads, the last one's numbers stay, never the two mixed.
  const last = useRef(null);
  const stale = groups.previousParams || traces.previousParams || agents.previousParams;
  const d = useMemo(() => {
    if (stale || !groups.data || !traces.data || !agents.data) return stale ? last.current : null;
    const list = agentsOf(agents.data);
    const exact = (block, field, span) => ({ ...block, groups: exactGroups(block.groups, list, field), sessions: tracesIn(traces.data, span) });
    return {
      ...groups.data,
      now: exact(groups.data.now, "now", win.w.now),
      // As on the Overview, nothing is compared where the span before held no traces.
      then: then && agents.data.before && tracesIn(traces.data, win.w.before) > 0 ? exact(then, "before", win.w.before) : null,
    };
  }, [stale, groups.data, traces.data, agents.data, then, win]);
  last.current = d;
  const read = { loading: groups.loading || traces.loading || agents.loading, error: groups.error || traces.error || agents.error };
  return { read, d };
}

// Fleet's pages as its sidebar lists them, for the phone's menu.
const PAGES = [["agents", "Agents and models"], ["runtimes", VIEW_LABEL.runtimes], ["endpoints", "Endpoints"]];

// The app's range, as the Overview and Findings pick it.
export function useSetRange() {
  const [, setParams] = useSearchParams();
  return (value) => setParams((p) => { const next = new URLSearchParams(p); next.set("range", value); return next; });
}

export function Head({ view, range, count, find, onFind, names, title = VIEW_LABEL[view] }) {
  const setRange = useSetRange();
  return (
    <header className="flex flex-wrap items-center gap-x-[12px] gap-y-[8px] pb-[8px] pt-[16px]">
      {/* On a phone the drawer starts shut, so the title is a menu of the lists. */}
      <div className="relative mr-auto flex items-baseline gap-[10px] rounded-[7px] max-[640px]:min-h-[44px] max-[640px]:items-center has-[select:focus-visible]:outline-2 has-[select:focus-visible]:outline-offset-2 has-[select:focus-visible]:outline-[color:var(--green)]">
        <h1 className="m-0 text-[22px] font-semibold leading-[1.15] tracking-[-0.02em] text-[color:var(--ink)]">{title}</h1>
        <span className={`text-[13px] tabular-nums ${MUTED}`}>{count == null ? "" : NUM.format(count)}</span>
        <select aria-label="Fleet list" value={view === "models" ? "agents" : view} onChange={(e) => setFleet({ view: e.target.value })} className="absolute inset-0 hidden h-full w-full cursor-pointer appearance-none opacity-0 max-[640px]:block" data-fleet-lists="">
          {PAGES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
      </div>
      <span className="w-[160px] shrink-0" data-fleet-range="">
        <DateRangePicker range={range} onRangeChange={setRange} />
      </span>
      <SearchInput
        size="sm"
        list={`fleet-find-${view}`}
        value={find}
        onChange={(value) => onFind(value)}
        placeholder={`Find ${title.toLowerCase()}`}
        className="w-[220px] pointer-coarse:h-[44px] pointer-coarse:[&_input]:text-[16px] max-[480px]:w-auto max-[480px]:min-w-0 max-[480px]:flex-[1_1_120px]"
        data-fleet-find=""
      />
      <datalist id={`fleet-find-${view}`}>{names.map((n) => <option key={n} value={n} />)}</datalist>
    </header>
  );
}

// One of a few, pressed, side by side; a finger's height on a touch screen.
export function Choice({ label, options, value, onChange, attr }) {
  return (
    <Tabs
      mode="toggle"
      label={label}
      items={options.map(([id, name]) => ({ id, label: name }))}
      value={value}
      onChange={onChange}
      itemAttrs={(item) => ({ [attr]: item.id })}
      className="pointer-coarse:[&>button]:h-[38px]"
    />
  );
}

function Failed({ read, d, view }) {
  return read.error && !d ? <p role="alert" className="m-0 py-[24px] text-[13px] text-[color:var(--severity-critical)]">{VIEW_LABEL[view]} could not be read. Reload to try again.</p> : null;
}

const CORNER = "pointer-events-none absolute h-[7px] w-[7px] border-[color:var(--ink)]";
// What is picked: a tick at each corner.
export function Corners() {
  return (
    <span aria-hidden="true">
      <span className={`${CORNER} -left-[4px] -top-[4px] border-l-[1.5px] border-t-[1.5px]`} />
      <span className={`${CORNER} -right-[4px] -top-[4px] border-r-[1.5px] border-t-[1.5px]`} />
      <span className={`${CORNER} -bottom-[4px] -left-[4px] border-b-[1.5px] border-l-[1.5px]`} />
      <span className={`${CORNER} -bottom-[4px] -right-[4px] border-b-[1.5px] border-r-[1.5px]`} />
    </span>
  );
}

function Pivots({ node, scopeNode, range, installed }) {
  const { explore, endpoints } = useFleetPivots();
  const pivot = node.unused ? null : exploreFor(scopeNode ? [scopeNode, node] : [node]);
  if (!pivot && !installed?.installed) return null;
  const what = pivot?.exact ? `${NUM.format(node.sessions)} traces` : node.level === "agent" && node.providers.length === 1 ? `${reachedAs(node.providers[0])} traces` : "Traces";
  return (
    <div className="flex flex-wrap gap-[8px]" data-fleet-pivots="">
      {installed?.installed ? (
        <button type="button" className={BUTTON} onClick={() => endpoints(installed.name)} data-fleet-endpoints="">
          <span className="tabular-nums">{NUM.format(installed.installed)} installed</span><span className={MUTED}>Endpoints</span>
        </button>
      ) : null}
      {pivot ? (
        <button type="button" className={BUTTON} onClick={() => explore(pivot.q, range)} data-fleet-explore="">
          <span className="tabular-nums">{what}</span><span className={MUTED}>Explore</span>
        </button>
      ) : null}
    </div>
  );
}

export function Tile({ name, label, now, then, on = false, onClick = null }) {
  const body = (
    <>
      <span className={`text-[12px] leading-[16px] ${MUTED}`}>{label}</span>
      <span className="flex flex-wrap items-baseline gap-x-[10px]">
        <span className={`min-h-[34px] text-[28px] font-medium leading-[34px] tracking-[-0.02em] tabular-nums ${INK}`}>{now == null ? "" : NUM.format(now)}</span>
        <span className={`text-[12.5px] tabular-nums ${now != null && bigChange(now, then) ? `font-medium ${INK}` : MUTED}`}>{now == null ? "" : change(now, then)}</span>
      </span>
      {on ? <Corners /> : null}
    </>
  );
  const box = "relative grid min-w-0 content-start gap-[4px] border-t border-[color:var(--line-strong)] px-[2px] pb-[12px] pt-[10px] text-left";
  return onClick
    ? <button type="button" aria-pressed={on} onClick={onClick} className={`${box} hover:bg-[color:var(--gray-fill)] ${ROW}`} data-fleet-tile={name}>{body}</button>
    : <div className={box} data-fleet-tile={name}>{body}</div>;
}

const BAND = 30;
const LANE = 18;
// The range's sessions split by kind, each part named in it where the name
// fits, else under it; a part with no sessions keeps its name.
function Band({ parts, pick, onPick }) {
  const ref = useRef(null);
  const { width } = useElementSize(ref);
  const laid = useMemo(() => split(parts, width, 2, 3, true), [parts, width]);
  const whole = parts.reduce((n, p) => n + p.sessions, 0);
  const text = laid.map(({ node }) => [node.name, shareText(node.sessions, whole)]);
  const widths = text.map(([name, share]) => textWidth(name, 12.5, 500) + 6 + textWidth(share, 12));
  const inside = laid.map((p, i) => p.len >= widths[i] + 16);
  const out = laid.map((_, i) => i).filter((i) => !inside[i]);
  const placed = placeLabels(out.map((i) => laid[i]), out.map((i) => widths[i]), width, 16, 3);
  const spot = new Map(out.map((i, k) => [i, placed[k]]));
  const lanes = Math.max(0, ...[...spot.values()].filter(Boolean).map((s) => s.lane + 1));
  const label = (i, quiet) => (
    <span className="whitespace-nowrap text-[12.5px] leading-[16px]">
      <span className={quiet ? MUTED : `font-medium ${INK}`}>{text[i][0]}</span>
      <span className={`ml-[6px] text-[12px] tabular-nums ${MUTED}`}>{text[i][1]}</span>
    </span>
  );
  return (
    <div ref={ref} className="relative w-full" style={{ height: BAND + (lanes ? 6 + lanes * LANE : 0) }} data-fleet-band="">
      {laid.map((p, i) => {
        const s = spot.get(i);
        const under = (left) => (s ? <span className="absolute" style={{ left, top: BAND + 6 + s.lane * LANE }}>{label(i, !p.node.sessions)}</span> : null);
        if (!p.node.sessions) return <Fragment key={p.node.id}>{under(s?.x)}</Fragment>;
        const on = pick === p.node.id;
        return (
          <button
            key={p.node.id}
            type="button"
            aria-pressed={on}
            aria-label={`${text[i][0]}, ${text[i][1]}`}
            onClick={() => onPick(p.node.id)}
            className={`absolute top-0 flex items-center bg-[color:var(--green)]/20 ${inside[i] ? "px-[8px]" : "px-0"} text-left hover:bg-[color:var(--green)]/30 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--green)]`}
            style={{ left: p.at, width: p.len, height: BAND }}
            data-fleet-band-part={p.node.id}
          >
            {inside[i] ? label(i, false) : under(s ? s.x - p.at : 0)}
            {on ? <Corners /> : null}
          </button>
        );
      })}
    </div>
  );
}

// The register while it is read: rows the height of a row with a line under
// its name, each fainter than the last, so the rows land where these stood.
export function RowsLoading({ label }) {
  return <SkeletonRows rows={8} rowClassName={`${LINE} h-[52px]`} cells={["w-[70%]", "ml-auto w-[36px]", "ml-auto w-[40px] max-[640px]:hidden", "w-[55%]"]} label={label} />;
}

// What a register holds when it has nothing to list, or nothing named like the
// search; `attr` marks the latter for tests.
export function Empty({ title, note, attr }) {
  return <div {...attr}><EmptyState className="my-[12px]" title={title} note={note} /></div>;
}

// The rows a fold still holds, as one row that unfolds the next of them.
export function More({ rest, noun, onMore, child = false }) {
  return (
    <button
      type="button"
      onClick={onMore}
      className={`${LINE} hover:bg-[color:var(--gray-fill)] ${ROW} ${child ? "min-h-[30px] py-[3px] pointer-coarse:min-h-[44px]" : "min-h-[44px] border-b border-[color:var(--line)] py-[8px]"}`}
      data-fleet-more={child ? "child" : "list"}
    >
      <span className={`text-[12.5px] ${MUTED} ${child ? "pl-[18px]" : ""}`}>{NUM.format(rest.count)} more {noun}</span>
      <span className={`text-right text-[12.5px] tabular-nums ${MUTED}`}>{rest.sessions == null ? "" : NUM.format(rest.sessions)}</span>
      <span className="max-[640px]:hidden" />
      <span />
    </button>
  );
}

// A part of a bar, as hovering it says it; `first` where none is named before
// it; `restName` the sessions none of the parts holds.
// Model families whose names the agent logos don't read on their own (Claude,
// Gemini, Grok, DeepSeek and Qwen already resolve by name), as provider tags.
const MAKERS = [[/^(gpt|chatgpt|o\d)/i, "openai"], [/^(mistral|ministral|magistral|codestral|devstral)/i, "mistral"]];
export const makerOf = (name) => MAKERS.find(([re]) => re.test(name))?.[1] ?? null;

function tip(s, noun, first, restName) {
  const n = Math.round(s.sessions);
  if (s.more) return `${NUM.format(s.more)} ${first ? "" : "more "}${noun}, ${NUM.format(n)} ${n === 1 ? "trace" : "traces"}`;
  return <>{s.rest ? restName : s.name}<span className={`ml-[8px] tabular-nums ${MUTED}`}>{NUM.format(n)}</span></>;
}

// A register row: its name over `sub` (a line or lines; the kinds it ran in
// where none is given), its sessions and change, and a bar split into what it
// used (`noun`), unfolding into those parts, then `detail` and `actions` (the
// agents page's pivots where none are given). With `mark`, its logo leads the
// name: a model's is its maker's, read from the name.
export function Row({ row, view, whole, scale, width, before, opened, onToggle, kidPages, onMoreKids, scopeNode, range, installed, plain, found, sub, noun = USED[view], restName = "No model", detail = null, actions, mark = false }) {
  const ref = useRef(null);
  useEffect(() => { if (found) ref.current?.scrollIntoView({ block: "center" }); }, [found]);
  const share = scale ? Math.min(1, row.sessions / scale) : 0;
  // Fewer named parts where the bar is too short to tell them apart, so a tail row stays one mark.
  const segs = useMemo(() => segmentsOf(row, Math.min(PARTS, Math.max(0, Math.floor((width * share) / 7) - 1))), [row, width, share]);
  const size = segs.reduce((n, s) => n + s.size, 0);
  const lead = segs[0];
  const tag = Boolean(lead?.name) && (width * share - (segs.length - 1)) * (lead.size / size) >= textWidth(lead.name, 11) + 14;
  const b = before ? before.get(row.id) || { sessions: 0, used: new Map() } : null;
  const words = sub !== undefined ? sub : plain ? "" : phrase(row.parts);
  const lines = (Array.isArray(words) ? words : [words]).filter(Boolean);
  const kids = opened ? paged(row.used, kidPages, STEP, STEP) : null;
  return (
    <div ref={ref} className={`border-b border-[color:var(--line)] ${found ? "bg-[color-mix(in_srgb,var(--ink)_10%,transparent)]" : ""}`} data-fleet-row={row.id}>
      <button
        type="button"
        aria-expanded={opened}
        aria-label={`${row.name}, ${NUM.format(row.sessions)} traces`}
        onClick={onToggle}
        className={`${LINE} min-h-[44px] py-[8px] hover:bg-[color:var(--gray-fill)] ${ROW}`}
      >
        <span className="flex min-w-0 items-start gap-[10px]">
          {mark ? <AgentIcon name={row.name} fallback={row.level === "model" ? makerOf(row.name) : null} className={`mt-[1px] h-[16px] w-[16px] shrink-0 ${row.unused ? MUTED : INK}`} /> : null}
          <span className="grid min-w-0 gap-[1px]">
            <span className={`[overflow-wrap:anywhere] text-[13px] leading-[18px] ${row.unused ? MUTED : INK}`}>{row.name}</span>
            {lines.map((w, i) => <span key={i} className={`text-[11.5px] leading-[16px] ${MUTED}`}>{w}</span>)}
          </span>
        </span>
        <span className={`text-right text-[12.5px] tabular-nums ${row.unused ? MUTED : INK}`}>{NUM.format(row.sessions)}</span>
        <span className={`text-right text-[12px] tabular-nums max-[640px]:hidden ${b && bigChange(row.sessions, b.sessions, whole) ? `font-medium ${INK}` : MUTED}`}>{change(row.sessions, b?.sessions)}</span>
        <span className="flex h-[18px] min-w-0 items-center" aria-hidden="true">
          {row.unused ? <span className={`text-[12px] ${MUTED}`}>{endpointsOf(row.installed)}</span> : (
            <span className="flex h-full gap-[1px]" style={{ width: `${share * 100}%` }}>
              {segs.map((s, i) => (
                <HoverTooltip key={s.id} placement="top" label={tip(s, noun, i === 0, restName)}>
                  <span className={`flex min-w-[2px] items-center overflow-hidden ${s.rest ? "bg-[color:var(--track-bg)]" : "bg-[color:var(--green)]/25"}`} style={{ flex: `${s.size} 1 0px` }} data-fleet-segment={s.id}>
                    {i === 0 && tag ? <span className={`whitespace-nowrap px-[6px] text-[11px] leading-[18px] ${INK}`}>{s.name}</span> : null}
                  </span>
                </HoverTooltip>
              ))}
            </span>
          )}
        </span>
      </button>
      {kids ? (
        <div className="pb-[12px]" data-fleet-opened={row.id}>
          {kids.shown.map((u) => (
            <div key={u.id} className={`${LINE} min-h-[30px] py-[3px]`} data-fleet-child={u.id}>
              <span className={`min-w-0 [overflow-wrap:anywhere] ${mark ? "pl-[26px]" : "pl-[18px]"} text-[12.5px] leading-[18px] ${INK}`}>{u.name}</span>
              <span className={`text-right text-[12.5px] tabular-nums ${INK}`}>{NUM.format(u.sessions)}</span>
              <span className={`text-right text-[12px] tabular-nums max-[640px]:hidden ${MUTED}`}>{change(u.sessions, b ? b.used.get(u.id) ?? 0 : null)}</span>
              <span className="flex h-[12px] items-center" aria-hidden="true">
                <span className="h-full min-w-[2px] bg-[color:var(--green)]/25" style={{ width: pct(u.sessions, scale) }} />
              </span>
            </div>
          ))}
          {kids.rest ? <More rest={kids.rest} noun={noun} onMore={onMoreKids} child /> : null}
          {detail ? <div className="pl-[26px] pt-[10px]">{detail}</div> : null}
          <div className="pl-[26px] pt-[8px]">
            {actions !== undefined ? actions : <Pivots node={row} scopeNode={scopeNode} range={range} installed={installed} />}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function FleetList({ view, range = DEFAULT_RANGE }) {
  const agents = view === "agents";
  const { read, d } = useBlock(range);
  // The kind (or installed and not used) picked, the order, and each subject's search.
  const [asked, setAsked] = useState(null);
  const [sort, setSort] = useState("most");
  const [finds, setFinds] = useState({});
  const [open, setOpen] = useState(() => new Set());
  // How far each fold is unfolded, by the list or row it closes.
  const [pages, setPages] = useState({});
  const [found, setFound] = useState(null);
  const track = useRef(null);
  const { width } = useElementSize(track);
  const now = d?.now;
  const then = d?.then;
  const installed = d?.installed || null;
  const band = useMemo(() => bandOf(now), [now]);
  const all = useMemo(() => totalsOf(now, null, installed), [now, installed]);
  // A pick this range has nothing of is dropped, so the list never empties with nothing to clear it.
  const pick = asked === "unused" ? (agents && all?.unused ? asked : null) : band.some((p) => p.id === asked && p.sessions) ? asked : null;
  const scope = useMemo(() => (pick && pick !== "unused" ? { by: "kind", id: pick } : null), [pick]);
  const totals = useMemo(() => totalsOf(now, scope, installed), [now, scope, installed]);
  const totalsThen = useMemo(() => totalsOf(then, scope, installed), [then, scope, installed]);
  const rows = useMemo(() => (pick === "unused" ? [] : rowsOf(view, now, scope, installed)), [view, now, scope, installed, pick]);
  const before = useMemo(() => beforeOf(view, then, scope), [view, then, scope]);
  const sorted = useMemo(() => sortRows(rows, sort, before), [rows, sort, before]);
  const idle = useMemo(() => (agents && !scope ? unusedRows(installed, now) : []), [agents, scope, installed, now]);
  const names = useMemo(() => [...new Set([...rows, ...idle].map((r) => r.name))].sort(), [rows, idle]);
  // Find narrows the list to the names holding its text, in the same order.
  const q = (finds[view] || "").trim().toLowerCase();
  const matched = useMemo(() => (q ? sorted.filter((r) => r.name.toLowerCase().includes(q)) : sorted), [sorted, q]);
  const idleShown = useMemo(() => (q ? idle.filter((r) => r.name.toLowerCase().includes(q)) : idle), [idle, q]);
  const listOf = (text) => `list:${view}:${pick || "all"}:${sort}:${text}`;
  const list = listOf(q);
  const { shown, rest } = paged(matched, pages[list] || 0);
  // The busiest row spans the bar column; every bar and part is on its scale.
  const scale = rows[0]?.sessions || 0;
  const keyOf = (id) => `row:${view}:${pick || "all"}:${id}`;
  const more = (key) => setPages((p) => ({ ...p, [key]: (p[key] || 0) + 1 }));
  const toggle = (key) => setOpen((was) => {
    const next = new Set(was);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  const onPick = (id) => setAsked(pick === id ? null : id);
  // Installed products are agents: from models, the pick shows them there.
  const onUnused = () => {
    setAsked(pick === "unused" ? null : "unused");
    if (!agents) setFleet({ view: "agents" });
  };
  // A whole name also unfolds the narrowed list as far as it and opens its row.
  const onFind = (value) => {
    setFinds((f) => ({ ...f, [view]: value }));
    const v = value.trim().toLowerCase();
    const within = v ? sorted.filter((r) => r.name.toLowerCase().includes(v)) : sorted;
    const at = v ? within.findIndex((r) => r.name.toLowerCase() === v) : -1;
    const hit = at >= 0 ? within[at] : v ? idle.find((r) => r.name.toLowerCase() === v) : null;
    if (!hit) return setFound(null);
    if (at >= 0) setPages((p) => ({ ...p, [listOf(v)]: Math.max(p[listOf(v)] || 0, pagesTo(at)) }));
    setOpen((was) => new Set(was).add(keyOf(hit.id)));
    setFound(`${view}:${hit.id}`);
  };
  // A link to one agent by name (the Overview's rows, `fleetSearch`) finds it
  // once the list is read, then leaves the address.
  const [params, setParams] = useSearchParams();
  const linked = useRef(agents ? params.get("fleetSearch") : null);
  useEffect(() => {
    if (!linked.current || !sorted.length) return;
    onFind(linked.current);
    linked.current = null;
    setParams((p) => { const next = new URLSearchParams(p); next.delete("fleetSearch"); return next; }, { replace: true });
  }, [sorted]);

  const busy = read.loading ? "true" : undefined;
  const scopeNode = scope ? { level: "kind", id: scope.id } : null;
  const row = (r) => {
    const key = keyOf(r.id);
    return (
      <Row
        key={r.id}
        row={r}
        view={view}
        whole={totals?.sessions || 0}
        scale={scale}
        width={width}
        before={before}
        opened={open.has(key)}
        onToggle={() => toggle(key)}
        kidPages={pages[key] || 0}
        onMoreKids={() => more(key)}
        scopeNode={scopeNode}
        range={range}
        installed={r.unused ? r : installed?.get(r.id) || null}
        plain={Boolean(scope)}
        found={found === `${view}:${r.id}`}
        mark
      />
    );
  };

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-y-auto" data-fleet-list={view}>
      <div className="w-full max-w-[1080px] pb-[48px]">
        <Head view={view} range={range} count={all?.[view]} find={finds[view] || ""} onFind={onFind} names={names} />
        <Failed read={read} d={d} view={view} />
        {read.error && !d ? null : (
          <>
            <div aria-busy={busy} className="grid grid-cols-4 gap-x-[20px] pt-[8px] max-[640px]:grid-cols-2 max-[640px]:gap-y-[14px]" data-fleet-tiles="">
              <Tile name="sessions" label="Traces" now={totals?.sessions} then={totalsThen?.sessions} />
              <Tile name="agents" label="Agents" now={totals?.agents} then={totalsThen?.agents} />
              <Tile name="models" label="Models" now={totals?.models} then={totalsThen?.models} />
              <Tile name="unused" label="Installed, not used" now={totals?.unused ?? null} then={totalsThen?.unused} on={pick === "unused"} onClick={totals?.unused ? onUnused : null} />
            </div>
            <div aria-busy={busy} className="pt-[22px]">
              {now ? <Band parts={band} pick={pick} onPick={onPick} /> : <div style={{ height: BAND }} />}
            </div>
            <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[8px] pb-[10px] pt-[30px]">
              <Choice label="Agents or models" options={[["agents", VIEW_LABEL.agents], ["models", VIEW_LABEL.models]]} value={view} onChange={(v) => setFleet({ view: v })} attr="data-fleet-subject" />
              <Choice label="Order" options={SORTS} value={sort} onChange={setSort} attr="data-fleet-sort" />
            </div>
            <div aria-busy={busy} data-fleet-register="">
              <div className={`${LINE} border-b border-[color:var(--line-strong)] pb-[6px] text-[11.5px] ${MUTED}`} aria-hidden="true">
                <span>{agents ? "Agent" : "Model"}</span>
                <span className="text-right">Traces</span>
                <span className="max-[640px]:hidden" />
                <span ref={track}>{agents ? "Models" : "Agents"}</span>
              </div>
              {read.loading && !d ? <RowsLoading label={`Loading ${view}…`} /> : null}
              {now && !rows.length && !idle.length ? <Empty title="No traces in this range" note="Agents and models appear here once Origin records an AI trace. Pick a longer range to look further back." /> : null}
              {q && (rows.length || idle.length) && !matched.length && !idleShown.length ? <Empty attr={{ "data-fleet-no-match": "" }} title={`No ${view} named like “${finds[view].trim()}”`} note={`Clear the search to see all ${view}.`} /> : null}
              {d ? shown.map(row) : null}
              {rest ? <More rest={rest} noun={view} onMore={() => more(list)} /> : null}
              {d && idleShown.length ? (
                <section aria-label="Installed, not used" data-fleet-section="unused">
                  {pick === "unused" ? null : <div className={`border-b border-[color:var(--line)] px-[8px] pb-[8px] pt-[22px] text-[12.5px] font-medium ${MUTED}`}>Installed, not used</div>}
                  {idleShown.map(row)}
                </section>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
